import { useState } from 'react';
import { api } from '@karozik/ui';
import type { AdminEvent } from '../types';
import { Field } from './Events';

function CopyRow({ label, url }: { label: string; url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 truncate rounded-xl bg-surface-2 px-3 py-2 text-sm">{url}</code>
      <button
        onClick={async () => {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="rounded-xl bg-surface-2 px-3 py-2 text-sm"
        aria-label={`Copier ${label}`}
      >
        {copied ? '✓' : 'Copier'}
      </button>
    </div>
  );
}

export function ShareTab({ event, onChange }: { event: AdminEvent; onChange(e: AdminEvent): void }) {
  const [wifi, setWifi] = useState(event.settings.wifi ?? { ssid: '', password: '', security: 'WPA' as const });
  const [saved, setSaved] = useState(false);

  const rotate = async (which: 'join' | 'kiosk') => {
    const msg =
      which === 'join'
        ? 'Régénérer le lien invités ? Les QR codes déjà imprimés ne fonctionneront plus (les invités déjà connectés gardent leur session).'
        : 'Régénérer le lien de la borne ?';
    if (!confirm(msg)) return;
    onChange(await api<AdminEvent>(`/api/admin/events/${event.id}/rotate`, { method: 'POST', json: { which } }));
  };

  return (
    <div className="space-y-6">
      <section className="rounded-3xl bg-surface p-5">
        <h2 className="font-display text-lg font-bold">QR code invités</h2>
        <p className="mt-1 text-sm text-ink-dim">
          Valable du {new Date(event.startsAt).toLocaleString('fr-FR')} au {new Date(event.endsAt).toLocaleString('fr-FR')}.
        </p>
        <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row sm:items-start">
          <img src={`/api/join/${event.joinToken}/qr.svg`} alt="QR code invités" className="h-48 w-48 rounded-2xl bg-white p-2" />
          <div className="w-full flex-1 space-y-3">
            <CopyRow label="lien invités" url={event.joinUrl} />
            <a href={`/api/admin/events/${event.id}/cards.pdf`} target="_blank" rel="noreferrer" className="bg-gradient-neon block rounded-2xl py-3 text-center font-bold">
              🖨️ Chevalets de table (PDF)
            </a>
            <button onClick={() => void rotate('join')} className="w-full rounded-xl bg-surface-2 py-2 text-sm text-ink-dim">
              Régénérer le lien
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-3xl bg-surface p-5">
        <h2 className="font-display text-lg font-bold">Borne (tablette du pupitre)</h2>
        <p className="mt-1 text-sm text-ink-dim">
          Ouvre ce lien sur la tablette Android dans <b>Fully Kiosk Browser</b> (plein écran verrouillé). Code animateur : onglet Réglages.
        </p>
        <div className="mt-3 space-y-2">
          <CopyRow label="lien borne" url={event.kioskUrl} />
          <button onClick={() => void rotate('kiosk')} className="w-full rounded-xl bg-surface-2 py-2 text-sm text-ink-dim">
            Régénérer le lien
          </button>
        </div>
      </section>

      <section className="rounded-3xl bg-surface p-5">
        <h2 className="font-display text-lg font-bold">Wi-Fi de la salle</h2>
        <p className="mt-1 text-sm text-ink-dim">Affiché en QR code sur l’écran géant et les chevalets : les invités s’y connectent en un scan.</p>
        <form
          className="mt-4 space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            const next = await api<AdminEvent>(`/api/admin/events/${event.id}`, {
              method: 'PATCH',
              json: { settings: { wifi: wifi.ssid ? wifi : null } },
            });
            onChange(next);
            setSaved(true);
            setTimeout(() => setSaved(false), 1500);
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nom du réseau (SSID)">
              <input value={wifi.ssid} onChange={(e) => setWifi({ ...wifi, ssid: e.target.value })} className="input" />
            </Field>
            <Field label="Mot de passe">
              <input value={wifi.password} onChange={(e) => setWifi({ ...wifi, password: e.target.value })} className="input" />
            </Field>
          </div>
          <Field label="Sécurité">
            <select value={wifi.security} onChange={(e) => setWifi({ ...wifi, security: e.target.value as 'WPA' })} className="input">
              <option value="WPA">WPA / WPA2 / WPA3</option>
              <option value="WEP">WEP</option>
              <option value="nopass">Ouvert</option>
            </select>
          </Field>
          <button className="w-full rounded-2xl bg-surface-3 py-3 font-semibold">{saved ? '✓ Enregistré' : 'Enregistrer'}</button>
        </form>
        {event.settings.wifi && (
          <img src={`/api/join/${event.joinToken}/wifi.svg`} alt="QR code Wi-Fi" className="mx-auto mt-4 h-32 w-32 rounded-xl bg-white p-1.5" />
        )}
      </section>
    </div>
  );
}
